import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useToast } from '@/hoc';
import { clearVoiceAdmin, getVoiceAdminEmail, getVoiceAdminRole, useAdminVoiceProvider, useAdminVoiceProviders, useCreateVoiceProvider, useDeleteVoiceProvider, useUpdateVoiceProvider, } from './voice-admin-api';
import { AudioLines, BookOpen, Building2, Coins, Cpu, FileText, Film, FolderTree, Images, LayoutDashboard, Layers, ListTree, Mic, Plus, ShieldCheck, Tags, Video, Wallet, } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Button, Modal } from '../ui';
import { Skeleton, SkeletonRegion } from '../ui/skeleton';
import { canOpenSection, defaultSectionFor, sectionsForRole } from './admin-access';
import AdminConsoleLayout from './admin-console-layout';
import { DashOverlay, ErrorNotice } from './admin-ui';
import InterviewModePanel from './interview-mode-panel';
// VA-28160: the capture-resolution bench is superseded by the calibration one.
// The old bench composed a fake "screen" with the webcam as a PiP tile, to
// reproduce a downscale that no longer happens — proctoring detects on the RAW
// webcam now, so there is no tile to shrink. The resolution control it existed
// for carries over; the composition does not.
import ProctoringCalibrationPanel from './proctoring-calibration-panel';
import RecordingBenchPanel from './recording-bench-panel';
import RecordingConfigPanel from './recording-config-panel';
import ProviderDetailView from './provider-detail-view';
import ProviderListPanel from './provider-list-panel';
import ProviderFormModal from './provider-form/provider-form-modal';
import DashboardPanel from './ai-usage/dashboard-panel';
import InterviewDetailPage from './ai-usage/interview-detail-page';
import ModelsPanel from './ai-usage/models-panel';
import PricingPanel from './ai-usage/pricing-panel';
import UsagePanel from './ai-usage/usage-panel';
import { useUsageBreakdown } from './ai-usage/ai-usage-api';
import VadTestPanel from './vad-test-panel';
import BargeInTestPanel from './barge-in-test-panel';
import ConversationTimingPanel from './conversation-timing-panel';
import VoiceProviderTestPanel, { VoiceProviderTestPanelSkeleton, } from './voice-provider-test-panel';
import { blogKeyOf, blogPageDescription, blogPageTitle, blogViewOf, } from './blog-studio/blog-routes';
// VA-29329: loaded on first visit to a Blog section, so it stays out of the main
// bundle that every page ships.
const BlogStudio = lazy(() => import('./blog-studio/blog-studio'));
const VAD_SUBTABS = [
    { key: 'turn', label: 'Turn detection (backend)' },
    { key: 'barge-in', label: 'Interruption config (browser)' },
    { key: 'timing', label: 'Conversation timing' },
];
const DEFAULT_ROUTE = { view: 'section', key: 'dashboard', org: '' };
const roleHome = (role) => ({
    view: 'section',
    key: defaultSectionFor(role),
    org: '',
});
const LEGACY_KEYS = { 'usage.interviews': 'usage.organizations' };
const parseRoute = (hash) => {
    var _a, _b, _c, _d;
    const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
    if (!parts.length)
        return DEFAULT_ROUTE;
    if (parts[0] === 'interview' && parts[1]) {
        // Slugs contain no slash today, but joining the tail is free and means a
        // slug that ever does cannot silently truncate to its first segment.
        const at = parts.indexOf('from', 2);
        const tail = at === -1 ? [] : parts.slice(at + 1);
        return {
            view: 'interview',
            slug: parts.slice(1, at === -1 ? undefined : at).join('/'),
            from: ((_a = LEGACY_KEYS[tail[0]]) !== null && _a !== void 0 ? _a : tail[0]) || undefined,
            org: tail[1] === 'org' ? (_b = tail[2]) !== null && _b !== void 0 ? _b : '' : '',
        };
    }
    if (parts[0] === 'blog' || parts[0].startsWith('blog.')) {
        return { view: 'section', key: parts[0], org: '', sub: parts.slice(1) };
    }
    return {
        view: 'section',
        // "Interview costs" became "Organizations"; old links still land.
        key: ((_c = LEGACY_KEYS[parts[0]]) !== null && _c !== void 0 ? _c : parts[0]),
        org: parts[1] === 'org' ? (_d = parts[2]) !== null && _d !== void 0 ? _d : '' : '',
    };
};
const orgPart = (org) => (org ? `/org/${encodeURIComponent(org)}` : '');
const routeToHash = (route) => {
    var _a;
    return route.view === 'interview'
        ? `#interview/${encodeURIComponent(route.slug)}${route.from ? `/from/${route.from}${orgPart(route.org)}` : ''}`
        : ((_a = route.sub) === null || _a === void 0 ? void 0 : _a.length)
            ? // A list's filters stay readable: #blog.posts/status=draft&q=ai
                `#${route.key}/${route.sub
                    .map((p) => encodeURIComponent(p).replace(/%3D/g, '=').replace(/%26/g, '&'))
                    .join('/')}`
            : `#${route.key}${orgPart(route.org)}`;
};
/**
 * Route state kept in sync with the fragment, in both directions.
 *
 * `hashchange` is what makes the browser's own Back button work — without it,
 * Back would change the URL and leave the console rendering the previous screen,
 * which is worse than having no URL state at all.
 */
const useConsoleRoute = () => {
    const [route, setRouteState] = useState(() => parseRoute(typeof window === 'undefined' ? '' : window.location.hash));
    useEffect(() => {
        const onHashChange = () => setRouteState(parseRoute(window.location.hash));
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);
    const setRoute = useCallback((next, opts) => {
        const hash = routeToHash(next);
        if (window.location.hash !== hash) {
            if (opts === null || opts === void 0 ? void 0 : opts.replace) {
                // Filters and search: one history entry per view, not per keystroke.
                window.history.replaceState(null, '', hash);
            }
            else {
                // Assigning the hash pushes a history entry, which is the point: Back
                // steps from an interview to the organization it came from.
                window.location.hash = hash;
            }
        }
        setRouteState(next);
    }, []);
    return [route, setRoute];
};
const SECTIONS = [
    // Grouped so the console reads as two concerns: what the platform SPENDS, and
    // how the platform is CONFIGURED.
    {
        key: 'dashboard',
        label: 'Dashboard',
        description: 'Spend, interviews and interview time — and where, on what and by whom it was spent.',
        icon: LayoutDashboard,
        group: 'Spend',
    },
    {
        key: 'usage',
        label: 'Usage & Cost',
        description: 'Three views of the same spend: totals by dimension, cost per interview, and every API call.',
        icon: Wallet,
        group: 'Spend',
        // Usage is three distinct questions. Stacked on one page you scrolled past
        // two to reach the third; as sub-sections each screen answers one.
        children: [
            {
                key: 'usage.breakdown',
                label: 'Spend breakdown',
                description: 'Where the money goes — total spend grouped by stage, feature, model, provider, organization or recruiter.',
                icon: Layers,
            },
            {
                key: 'usage.organizations',
                label: 'Organizations',
                description: "Each customer's spend — open one for its interviews, or search every organization's interviews.",
                icon: Building2,
            },
            {
                key: 'usage.events',
                label: 'API calls',
                description: 'Every individual AI call, newest first — open one to see what it consumed and how it was priced.',
                icon: ListTree,
            },
        ],
    },
    {
        key: 'models',
        label: 'Models',
        description: 'Which model serves each feature, and the registry of models the platform can use.',
        icon: Cpu,
        group: 'Configuration',
    },
    {
        key: 'pricing',
        label: 'Pricing',
        description: 'Provider rate card. Publishing a rate never changes historical cost.',
        icon: Coins,
        group: 'Configuration',
    },
    {
        key: 'providers',
        label: 'Voice Providers',
        description: 'Manage the dynamic STT/TTS providers available when scheduling interviews.',
        icon: Mic,
        group: 'Configuration',
    },
    {
        key: 'interview-mode',
        label: 'Interview Modes',
        description: 'Which engine new interviews are scheduled with. Interviews already scheduled keep their mode.',
        icon: Layers,
        group: 'Configuration',
    },
    {
        key: 'recording',
        label: 'Recording Quality',
        description: 'Resolution, frame rate and bitrate ceilings applied to every interview recording.',
        icon: Video,
        group: 'Configuration',
    },
    {
        key: 'blog',
        label: 'Blog',
        description: 'Write, publish and organise blog posts.',
        icon: BookOpen,
        group: 'Content',
        children: [
            {
                key: 'blog.posts',
                label: 'Posts',
                description: 'Every studio post: search, filter, publish and delete.',
                icon: FileText,
            },
            {
                key: 'blog.categories',
                label: 'Categories',
                description: 'One per post. Readers filter /blog by them.',
                icon: FolderTree,
            },
            {
                key: 'blog.tags',
                label: 'Tags',
                description: 'Topics a post can carry several of.',
                icon: Tags,
            },
            {
                key: 'blog.media',
                label: 'Media',
                description: 'Images uploaded for covers and social sharing.',
                icon: Images,
            },
        ],
    },
    {
        key: 'proctoring',
        label: 'Proctoring',
        description: 'Test proctoring face detection and tune the live capture resolution.',
        icon: ShieldCheck,
        group: 'Diagnostics',
    },
    {
        key: 'recording-bench',
        label: 'Recording Bench',
        description: 'Record a sample with a candidate configuration and compare it. Nothing is uploaded.',
        icon: Film,
        group: 'Diagnostics',
    },
    {
        key: 'vad',
        label: 'VAD / Turn Detection',
        description: 'Test the neural VAD (turn detection) against real speech and room noise.',
        icon: AudioLines,
        group: 'Diagnostics',
    },
];
/** A section's sidebar name, children included — for "Back to …" labels. */
const sectionLabel = (key) => {
    var _a, _b;
    return (_b = (_a = SECTIONS.flatMap((sec) => { var _a; return [sec, ...((_a = sec.children) !== null && _a !== void 0 ? _a : [])]; }).find((sec) => sec.key === key)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'Dashboard';
};
const VoiceProviderManagement = () => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { showToast } = useToast();
    // VA-29489: a blog admin sees only the Blog sections. The gate refreshed the
    // stored role from the session before rendering this.
    const role = getVoiceAdminRole();
    const isSuperAdmin = role === 'super_admin';
    const { data: providers, isLoading, error: providersError, refetch: refetchProviders, } = useAdminVoiceProviders(isSuperAdmin);
    const createMut = useCreateVoiceProvider();
    const updateMut = useUpdateVoiceProvider();
    const deleteMut = useDeleteVoiceProvider();
    const [modal, setModal] = useState({ mode: 'closed' });
    const [vadSubTab, setVadSubTab] = useState('turn');
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Holds an id, not a row: the list carries summaries and the test panel needs
    // the real spec (endpoint, sample rate) to run against.
    const [testProviderId, setTestProviderId] = useState(null);
    // Spend is what an operator opens this console to see, and the Dashboard
    // answers "how are we doing" without them having to ask a question first.
    const [urlRoute, setRoute] = useConsoleRoute();
    // A route this role may not open (a bookmark, a typed hash, an interview)
    // shows the role's landing section instead, and the URL is corrected below.
    const route = urlRoute.view === 'section' ? (canOpenSection(role, urlRoute.key) ? urlRoute : roleHome(role))
        : isSuperAdmin ? urlRoute : roleHome(role);
    useEffect(() => {
        if (route !== urlRoute)
            setRoute(route, { replace: true });
    }, [route, urlRoute, setRoute]);
    // Settings handed over by "Test a config in the bench", so the bench opens on
    // them instead of on its own defaults.
    const [benchPreset, setBenchPreset] = useState(undefined);
    // An open interview keeps the screen it came from lit in the sidebar.
    const tab = route.view === 'section' ? route.key : (_a = route.from) !== null && _a !== void 0 ? _a : 'dashboard';
    const setTab = (key) => setRoute({ view: 'section', key, org: '' });
    const blogSub = route.view === 'section' ? (_b = route.sub) !== null && _b !== void 0 ? _b : [] : [];
    const blogGo = (view, sub = [], opts) => setRoute({ view: 'section', key: blogKeyOf(view), org: '', sub }, opts);
    // An open interview reports its candidate and organization, so the header
    // breadcrumb reads  Usage & Cost › Organizations › <org> › <candidate>  and
    // every crumb opens the matching screen. Stored WITH its slug and used only
    // while that interview is open: a "clear on change" effect raced a cached
    // page, which reports before the parent's effect runs, and wiped it.
    const [reportedMeta, setReportedMeta] = useState(null);
    const openSlug = route.view === 'interview' ? route.slug : null;
    const interviewMeta = reportedMeta && reportedMeta.slug === openSlug ? reportedMeta : null;
    const setInterviewMeta = useCallback((meta) => openSlug && setReportedMeta(Object.assign({ slug: openSlug }, meta)), [openSlug]);
    // An organization opened in Usage & Cost → Organizations is a page of its
    // own: its name heads the breadcrumb, and "Organizations" is the way back.
    // Same (cached) query the page's organization picker already makes.
    const openOrg = route.view === 'section' && route.key === 'usage.organizations' ? route.org : '';
    const orgNames = useUsageBreakdown({ group_by: 'organization' }, isSuperAdmin && !!openOrg);
    const openOrgName = openOrg
        ? (_e = (_d = (_c = orgNames.data) === null || _c === void 0 ? void 0 : _c.rows.find((r) => r.key === openOrg)) === null || _d === void 0 ? void 0 : _d.label) !== null && _e !== void 0 ? _e : (openOrg === '__unattributed__' ? 'Unattributed' : openOrg)
        : null;
    const interviewCrumbs = route.view === 'interview' && (interviewMeta === null || interviewMeta === void 0 ? void 0 : interviewMeta.organizationId)
        ? [
            {
                label: interviewMeta.organizationName || interviewMeta.organizationId,
                onClick: () => {
                    var _a, _b;
                    return setRoute(((_a = route.from) === null || _a === void 0 ? void 0 : _a.startsWith('usage'))
                        ? // In Usage & Cost an organization means its interviews.
                            { view: 'section', key: 'usage.organizations', org: interviewMeta.organizationId }
                        : { view: 'section', key: (_b = route.from) !== null && _b !== void 0 ? _b : 'dashboard', org: interviewMeta.organizationId });
                },
            },
        ]
        : [];
    // Read-only detail. Previously the ONLY way into a provider's config was the
    // edit form, i.e. a screen where a stray keystroke changes live behaviour.
    const [viewProvider, setViewProvider] = useState(null);
    // Logout is owned by the console header now. clearing + dispatching the
    // shared event bounces the gate (which listens for it) back to its login.
    const handleLogout = () => {
        clearVoiceAdmin();
        window.dispatchEvent(new Event('voice-admin-unauthorized'));
    };
    const openCreate = () => setModal({ mode: 'create' });
    // The list is a SUMMARY now, so the form can no longer be populated from the
    // row — it fetches the provider's full record and fills in when it lands.
    const editingProviderId = modal.mode === 'edit' || modal.mode === 'duplicate' ? modal.providerId : null;
    const { data: editingProvider, isLoading: loadingEditing } = useAdminVoiceProvider(editingProviderId);
    const { data: testProvider, error: testProviderError, refetch: refetchTestProvider, } = useAdminVoiceProvider(testProviderId);
    const closeModal = () => setModal({ mode: 'closed' });
    const handleSave = (payload) => {
        const onSuccess = (verb) => {
            showToast({ message: `Provider ${verb}`, status: 'success' });
            closeModal();
        };
        const onError = (e) => {
            var _a, _b, _c;
            const apiErrors = (e === null || e === void 0 ? void 0 : e.errors) || ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) || ((_c = e === null || e === void 0 ? void 0 : e.data) === null || _c === void 0 ? void 0 : _c.errors);
            showToast({
                message: Array.isArray(apiErrors)
                    ? apiErrors.join('; ')
                    : (e === null || e === void 0 ? void 0 : e.error) || (e === null || e === void 0 ? void 0 : e.message) || 'Save failed',
                status: 'error',
            });
        };
        if (modal.mode === 'edit') {
            updateMut.mutate({ providerId: modal.providerId, data: payload }, { onSuccess: () => onSuccess('updated'), onError });
        }
        else {
            // Duplicate is a CREATE with a pre-filled body.
            createMut.mutate(payload, { onSuccess: () => onSuccess('created'), onError });
        }
    };
    const handleDelete = (providerId) => {
        deleteMut.mutate(providerId, {
            onSuccess: () => {
                showToast({ message: 'Provider deleted', status: 'success' });
                setConfirmDelete(null);
            },
            onError: (e) => showToast({ message: (e === null || e === void 0 ? void 0 : e.message) || 'Delete failed', status: 'error' }),
        });
    };
    const saving = createMut.isPending || updateMut.isPending;
    return (_jsxs(_Fragment, { children: [_jsxs(AdminConsoleLayout, { sections: sectionsForRole(SECTIONS, role), activeKey: tab, onSelect: (k) => setTab(k), adminEmail: getVoiceAdminEmail(), onLogout: handleLogout, pageTitle: route.view === 'interview'
                    ? (_f = interviewMeta === null || interviewMeta === void 0 ? void 0 : interviewMeta.title) !== null && _f !== void 0 ? _f : route.slug
                    : openOrgName !== null && openOrgName !== void 0 ? openOrgName : blogPageTitle(tab, blogSub), pageCrumbs: interviewCrumbs, pageDescription: route.view === 'interview'
                    ? 'One interview, down to the models that ran behind it.'
                    : openOrgName
                        ? "This organization's interviews and what each one cost."
                        : blogPageDescription(tab, blogSub), headerAction: tab === 'providers' ? (_jsx(Button, { variant: 'primary', size: 'sm', onClick: openCreate, rightIcon: _jsx(Plus, { className: 'h-4 w-4' }), children: "Add Provider" })) : undefined, children: [tab === 'interview-mode' && _jsx(InterviewModePanel, {}), tab === 'recording' && (_jsx(RecordingConfigPanel, { onOpenBench: (preset) => {
                            // Hand the bench the settings on screen so the operator does not
                            // retype them — and cannot accidentally test something else.
                            setBenchPreset(preset);
                            setRoute({ view: 'section', key: 'recording-bench', org: '' });
                        } })), tab === 'recording-bench' && _jsx(RecordingBenchPanel, { preset: benchPreset }), tab === 'proctoring' && (_jsx("div", { className: 'flex h-full min-h-[520px] flex-col overflow-hidden rounded-2xl border border-dash-border bg-dash-card shadow-sm', children: _jsx(ProctoringCalibrationPanel, {}) })), tab === 'vad' && (_jsxs("div", { className: 'min-h-[520px] overflow-y-auto rounded-2xl border border-dash-border bg-dash-card shadow-sm', children: [_jsx("div", { role: 'tablist', className: 'flex gap-1 border-b border-dash-border px-4 pt-3', children: VAD_SUBTABS.map((t) => (_jsx("button", { type: 'button', role: 'tab', "aria-selected": vadSubTab === t.key, onClick: () => setVadSubTab(t.key), className: `-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${vadSubTab === t.key
                                        ? 'border-dash-accent text-dash-accent'
                                        : 'border-transparent text-dash-muted hover:text-dash-text'}`, children: t.label }, t.key))) }), vadSubTab === 'turn' && _jsx(VadTestPanel, {}), vadSubTab === 'barge-in' && (
                            /* VA-28665: interruption tuning — a separate detector from turn detection. */
                            _jsx("div", { className: 'p-4', children: _jsx(BargeInTestPanel, {}) })), vadSubTab === 'timing' && (
                            /* VA-28992 / VA-28993: thinking time and the final no-new-question window. */
                            _jsx("div", { className: 'p-4', children: _jsx(ConversationTimingPanel, {}) }))] })), route.view === 'interview' ? (_jsx(InterviewDetailPage, { interviewSlug: route.slug, 
                        // Back goes exactly where the interview was opened from — that
                        // organization's list when it came from one.
                        backLabel: route.org && (interviewMeta === null || interviewMeta === void 0 ? void 0 : interviewMeta.organizationName)
                            ? interviewMeta.organizationName
                            : sectionLabel((_g = route.from) !== null && _g !== void 0 ? _g : 'dashboard'), onBack: () => { var _a, _b; return setRoute({ view: 'section', key: (_a = route.from) !== null && _a !== void 0 ? _a : 'dashboard', org: (_b = route.org) !== null && _b !== void 0 ? _b : '' }); }, onLoaded: setInterviewMeta })) : (_jsxs(_Fragment, { children: [tab === 'dashboard' && (_jsx(DashboardPanel, { organizationId: route.view === 'section' ? route.org : '', onOrganizationChange: (org) => setRoute({ view: 'section', key: 'dashboard', org }), onOpenInterview: (slug) => setRoute({
                                    view: 'interview', slug, from: 'dashboard',
                                    org: route.view === 'section' ? route.org : '',
                                }) })), (tab === 'usage' || tab.startsWith('usage.')) && (_jsx(UsagePanel, { view: tab === 'usage' ? 'breakdown' : tab.split('.')[1], 
                                // The organization rides along between tabs and into an interview,
                                // so Back from an interview returns to that customer's list.
                                onViewChange: (v) => setRoute({
                                    view: 'section',
                                    key: `usage.${v}`,
                                    org: route.view === 'section' ? route.org : '',
                                }), organizationId: route.view === 'section' ? route.org : '', onOrganizationChange: (org) => setRoute({ view: 'section', key: tab === 'usage' ? 'usage.breakdown' : tab, org }), onOpenInterview: (slug) => setRoute({
                                    view: 'interview',
                                    slug,
                                    from: tab === 'usage' ? 'usage.breakdown' : tab,
                                    org: route.view === 'section' ? route.org : '',
                                }) })), (tab === 'blog' || tab.startsWith('blog.')) && (_jsx(Suspense, { fallback: _jsx(BlogStudioSkeleton, {}), children: _jsx(BlogStudio, { view: blogViewOf(tab), sub: blogSub, go: blogGo }) })), tab === 'models' && _jsx(ModelsPanel, {}), tab === 'pricing' && _jsx(PricingPanel, {}), tab === 'providers' && (_jsx(ProviderListPanel, { providers: providers !== null && providers !== void 0 ? providers : [], loading: isLoading, error: providers ? null : providersError, onRetry: () => refetchProviders(), onView: setViewProvider, onEdit: (providerId) => setModal({ mode: 'edit', providerId }), onDuplicate: (providerId) => setModal({ mode: 'duplicate', providerId }), onTest: (p) => setTestProviderId(p.provider_id), onDelete: setConfirmDelete, onCreate: openCreate }))] }))] }), _jsx(ProviderDetailView, { providerId: viewProvider, onClose: () => setViewProvider(null), onEdit: (providerId) => {
                    setViewProvider(null);
                    setModal({ mode: 'edit', providerId });
                }, onDuplicate: (providerId) => {
                    setViewProvider(null);
                    setModal({ mode: 'duplicate', providerId });
                } }), modal.mode !== 'closed' && (_jsx(ProviderFormModal, { mode: modal.mode, initial: modal.mode === 'create' ? null : editingProvider !== null && editingProvider !== void 0 ? editingProvider : null, loading: modal.mode !== 'create' && loadingEditing, saving: saving, onSave: handleSave, onClose: closeModal }, modal.mode === 'create' ? 'create' : modal.providerId + modal.mode)), testProviderId && (_jsx(Modal, { isOpen: true, title: `Test provider — ${(testProvider === null || testProvider === void 0 ? void 0 : testProvider.label) || testProviderId}`, onClose: () => setTestProviderId(null), onSave: () => setTestProviderId(null), contentWidth: '!max-w-4xl', containerPadding: 'px-8 py-6', showSaveButton: false, cancelLabel: 'Close', children: _jsx(DashOverlay, { children: testProvider ? (_jsx(VoiceProviderTestPanel, { provider: testProvider })) : testProviderError ? (_jsx(ErrorNotice, { title: 'Could not load this provider.', error: testProviderError, onRetry: () => refetchTestProvider() })) : (_jsx(VoiceProviderTestPanelSkeleton, {})) }) })), confirmDelete && (_jsx(Modal, { isOpen: true, title: 'Delete voice provider', onClose: () => setConfirmDelete(null), onSave: () => handleDelete(confirmDelete), contentWidth: 'w-full max-w-md', containerPadding: 'px-6 py-5', children: _jsx(DashOverlay, { children: _jsxs("p", { className: 'text-sm text-dash-text', children: ["Delete provider ", _jsx("span", { className: 'font-semibold', children: confirmDelete }), "? Interviews already scheduled with it will fall back to the default provider."] }) }) }))] }));
};
/** The Blog Studio's header and tiles while its chunk loads. */
const BlogStudioSkeleton = () => (_jsxs(SkeletonRegion, { label: 'Loading the blog studio', className: 'space-y-4', children: [_jsx(Skeleton, { className: 'h-6 w-48 rounded' }), _jsx("div", { className: 'grid grid-cols-1 gap-2.5 aiSm:grid-cols-2 aiLg:grid-cols-3 aiXl:grid-cols-6', children: Array.from({ length: 6 }).map((_, i) => (_jsx(Skeleton, { className: 'h-[86px] rounded-xl' }, i))) }), _jsx(Skeleton, { className: 'h-72 rounded-xl' })] }));
export default VoiceProviderManagement;
